import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Film, Share2, Eye } from "lucide-react";
import api from "../lib/api";
import { useLang, BLOG_INFO } from "../context/LangContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BlogBadge from "../components/BlogBadge";
import Markdown from "../components/Markdown";
import Breadcrumbs from "../components/Breadcrumbs";
import JsonLd from "../components/JsonLd";
import PromoBlock from "../components/PromoBlock";
import { SocialPostCard } from "../components/PreviewContent";

export default function ArticlePage() {
  const { blog, slug } = useParams();
  const { t, lang } = useLang();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/public/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (blog) api.get(`/public/articles?blog=${blog}&page_size=4`).then((r) => setRelated((r.data.items || []).filter((a) => a.slug !== slug).slice(0, 3))).catch(() => {});
  }, [blog, slug]);

  const _bname = article ? (lang === "pt" ? BLOG_INFO[article.blog]?.pt : BLOG_INFO[article.blog]?.en) : "";
  useDocumentTitle(
    article ? `${article.title} · ${_bname} · ReiMultiBlog` : "ReiMultiBlog",
    article ? ((article.seo && article.seo.meta_description) || article.excerpt) : undefined
  );

  if (notFound) {
    return (
      <div className="App"><Navbar />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="text-muted-foreground">{t("Artigo não encontrado.", "Article not found.")}</p>
          <Link to={`/blog/${blog}`} className="text-primary underline mt-4 inline-block">{t("Voltar", "Back")}</Link>
        </div><Footer />
      </div>
    );
  }
  if (!article) return <div className="App"><Navbar /><div className="max-w-3xl mx-auto px-4 py-24 text-muted-foreground">{t("A carregar…", "Loading…")}</div></div>;

  const info = BLOG_INFO[article.blog];

  return (
    <div className="App relative">
      <Navbar />
      <JsonLd data={{ "@context": "https://schema.org", "@type": "Article", headline: article.title, image: article.images?.hero ? [article.images.hero] : undefined, datePublished: article.published_at, author: { "@type": "Person", name: "ReiMultiBlog" }, publisher: { "@type": "Organization", name: "ReiMultiBlog" }, description: (article.seo && article.seo.meta_description) || article.excerpt } } />
      {article.images?.hero && (
        <div className="relative h-[42vh] overflow-hidden border-b border-white/10">
          <img src={article.images.hero} alt={article.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>
      )}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <Breadcrumbs items={[{ label: lang === "pt" ? info?.pt : info?.en, to: `/blog/${article.blog}` }, { label: article.title }]} />
        <Link to={`/blog/${article.blog}`} className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
          <ArrowLeft className="h-4 w-4" /> {lang === "pt" ? info?.pt : info?.en}
        </Link>
        <BlogBadge blog={article.blog} />
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">{article.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span>{new Date(article.published_at).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
          <span className="inline-flex items-center gap-1.5" data-testid="article-views">
            <Eye className="h-4 w-4" />{article.views || 0} {t("visualizações", "views")}
          </span>
        </div>
        <div className="mt-8 pb-8">
          <Markdown>{article.content}</Markdown>
        </div>

        {article.videos?.length > 0 && (
          <div className="mt-8 border-t border-white/10 pt-8">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold mb-4"><Film className="h-5 w-5 text-primary" />{t("Vídeo", "Video")}</h2>
            {article.videos.map((v) => (
              <div key={v.id} className="rounded-2xl border border-white/10 bg-card p-5">
                {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full max-h-72 object-cover rounded-xl mb-4" />}
                <h3 className="font-semibold text-lg">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-300 whitespace-pre-wrap">{v.script}</p>
              </div>
            ))}
          </div>
        )}

        {article.social_posts?.length > 0 && (
          <div className="mt-8 border-t border-white/10 pt-8 pb-4">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold mb-4"><Share2 className="h-5 w-5 text-primary" />{t("Posts sociais", "Social posts")}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {article.social_posts.map((p) => (
                <SocialPostCard key={p.id} post={{ platform: p.platform, content: p.content, hashtags: (p.media || {}).hashtags || [] }} />
              ))}
            </div>
          </div>
        )}
        <div className="mt-10 border-t border-white/10 pt-8 max-w-md"><PromoBlock /></div>

        {related.length > 0 && (
          <div className="mt-10 border-t border-white/10 pt-8 pb-4">
            <h2 className="font-heading text-2xl font-bold mb-4">{t("Artigos relacionados", "Related articles")}</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {related.map((a) => (
                <Link key={a.id} to={`/blog/${a.blog}/${a.slug}`} data-testid={`related-${a.id}`} className="group rounded-xl border border-white/10 bg-card overflow-hidden">
                  {a.images?.hero && <img src={a.images.hero} alt={a.title} className="h-28 w-full object-cover" />}
                  <div className="p-3"><h3 className="text-sm font-semibold leading-snug group-hover:text-primary transition-colors line-clamp-2">{a.title}</h3></div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
      <Footer />
    </div>
  );
}
